<template>
  <div class="case-medication-list">
    <switch-tree
      :items="medicationItems"
      :expanded-map="state.expandedMap"
      title="Medication List"
      subtitle="(select a medication to modify it)"
      :loading="loading"
      :selected="selectedMedication ? selectedMedication.id : null"
      :readonly="readonly"
      @toggle="toggleItem"
      @select="selectItem"
      @action="performAction"
    >
      <template slot="first-item-slot">
        <div class="new-med-row">
          <md-button
            class="md-primary md-outlined-button add-button"
            aria-label="Add New Medication"
            data-testid="add-new-medication-button"
            :disabled="readonly"
            @click="showNewMedDialog = true"
          >
            <md-icon>add</md-icon>
            New Medication
          </md-button>
          <help-tooltip v-if="infusionCountError" type="error" aria-label="Too Many Infusion Routes Error" data-testid="too-many-infusion-routes-error">
            {{ infusionCountError.message }}
          </help-tooltip>
        </div>
      </template>
    </switch-tree>
    <new-med-dialog
      :show="showNewMedDialog"
      data-testid="new-medication-dialog-container"
      @cancel="cancelNewMedication"
      @submit="submitNewMedication"
    />
    <new-med-dialog
      :show="showNewDosageDialog"
      :group="state.newDosageGroup"
      data-testid="new-dosage-form-dialog-container"
      @cancel="cancelNewDosage"
      @submit="submitNewDosage"
    />
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import Chance from 'chance';
import { dosageForms, unitTypes, medicationGroups } from '~lib/common/config/medication';

import HelpTooltip from '~lib/common/components/HelpTooltip';
import SwitchTree from '../../common/SwitchList/SwitchTree';

import NewMedDialog from './NewMedDialog';

const chance = new Chance();

export default {
  name: 'case-medication-list',
  components: {
    HelpTooltip,
    SwitchTree,
    NewMedDialog,
  },
  props: {
    medications: {
      type: Array,
      required: true,
    },
    dosageForms: {
      type: Array,
      default: () => null,
    },
    customMedications: {
      type: Array,
      default: () => null,
    },
    customGroups: {
      type: Array,
      default: () => null,
    },
    language: {
      type: String,
      default: 'en',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    selectedMedication: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      showNewMedDialog: false,
      showNewDosageDialog: false,
      state: {
        sortedMeds: [],
        expandedMap: {},
        newDosageGroup: null,
      },
    };
  },
  computed: {
    medicationItems() {
      const groupedMeds = groupBy(
        this.state.sortedMeds.filter(med => {
          if (!med.group || !med.defaultConcentration || !med.dosageForm) {
            return false;
          }
          return true;
        }),
        'group'
      );
      return Object.values(groupedMeds).map(meds => {
        const med0 = meds[0];
        const group = this.findGroup(med0.group);
        const children = [ this.getNewDosageAction(group, med0.id) ];
        for (const med of meds) {
          children.push(this.getDosageFormItem(med))
        }
        return this.getMedGroupItem(group, children);
      });
    },
    allMeds() {
      return this.medications.concat(this.customMedications || []);
    },
    enabledMedicationIds() {
      return (this.dosageForms || []).map(df => df.medicationId);
    },
    infusionCountError() {
      let errors = []
      if (this.$store) {
        errors = get(this.$store.getters['validation/errorsByFieldAndId'], 'medications.infusion-count', []);
      }
      return errors.length > 0 ? errors[0] : null;
    },
  },
  watch: {
    medications() {
      this.sortMedications();
    },
    customMedications() {
      this.sortMedications();
    },
  },
  methods: {
    getMedGroupItem(group, children) {
      const on = children.some(child => child.on);
      const error = children.some(child => child.error);
      const warning = children.some(child => child.warning);
      const tooltip = error ? 'Dosage form has error' : warning ? 'Dosage form has warning' : undefined;
      return {
        key: group.key,
        label: group.label.en,
        on,
        children,
        error,
        warning,
        tooltip
      };
    },
    getDosageFormItem(med) {
      let medicationErrors = [];
      let medicationWarnings = [];
      let label;
      const dosageForm = this.dosageForms.find(df => df.medicationId === med.id);
      if (dosageForm) {
        label = this.getDosageFormLabel(dosageForm.dosageFormType, dosageForm.concentration);
        medicationErrors = uniqBy(
          get(
            this.$store.getters['validation/errorsByFieldAndId'],
            `dosageForms.${dosageForm.id}`,
          ),
          'message',
        );
        medicationWarnings = uniqBy(
          get(
            this.$store.getters['validation/visibleWarningsByFieldAndId'],
            `dosageForms.${dosageForm.id}`,
          ),
          'message',
        );
      } else {
        label = this.getDosageFormLabel(med.dosageForm, med.defaultConcentration);
        medicationErrors = uniqBy(
          get(this.$store.getters['validation/errorsByFieldAndId']['medications'], med.id),
          'message',
        );
        medicationWarnings = uniqBy(
          get(this.$store.getters['validation/visibleWarningsByFieldAndId']['medications'], med.id),
          'message',
        );
      }
      return {
        key: med.id,
        label,
        on: this.enabledMedicationIds.includes(med.id),
        error: !!medicationErrors.length,
        warning: !!medicationWarnings.length,
        tooltip: medicationErrors.length
          ? medicationErrors[0].message
          : medicationWarnings.length
          ? medicationWarnings[0].message
          : null,
      };
    },
    getNewDosageAction(medGroup, medicationId) {
      return {
        label: '+ New Dosage Form',
        key: `${medGroup.key}-new-dosage`,
        disabled: this.readonly,
        action: {
          groupKey: medGroup.key,
          medicationId
        }
      };
    },
    getDosageFormLabel(dosageFormType, concentration) {
      const df = dosageForms.find(form => form.key === dosageFormType);
      const units1 = unitTypes.find(unit => unit.key === concentration.numeratorUnits);
      if (df.unitType2) {
        const units2 = unitTypes.find(unit => unit.key === df.unitType2);
        return `${df.label.en} (${concentration.numeratorValue}${units1.label.en}/${units2.label.en})`;
      } else if (df.key === 'SUSPENSION' || df.key === 'SOLUTION') {
        const denominatorValue = +concentration.denominatorValue !== 1 ? concentration.denominatorValue : '';
        return `${df.label.en} (${concentration.numeratorValue}${units1.label.en}/${denominatorValue}mL)`;
      }
    },
    toggleItem(item, params) {
      if (params.isChild) {
        this.toggleMedications([item], params.value);
      } else {
        this.toggleMedications(item.children, params.value);
      }
    },
    toggleMedications(medItems, toggleOn) {
      let updatedDosageForms = this.dosageForms || [];
      if (toggleOn) {
        for (const medItem of medItems) {
          if (!medItem.action && !updatedDosageForms.find(df => df.medicationId === medItem.key)) {
            const med = this.findMedication(medItem.key);
            updatedDosageForms.push({
              id: chance.guid(),
              medicationId: med.id,
              description: med.description,
              dosageFormType: med.dosageForm,
              concentration: med.defaultConcentration,
              enabledRoutes: this.getDefaultRoutes(med),
              administeringUnits: [ this.getDefaultAdministeringUnits(med) ],
            });
            this.$analytics.event('Authoring Tool', 'medications_name_selected', med.name.en);
          }
        }
      } else {
        updatedDosageForms = updatedDosageForms.filter(df => {
          return !medItems.find(medItem => medItem.key === df.medicationId)
        });
      }
      this.$emit('updateCase', { dosageForms: updatedDosageForms });
    },
    toggleAllMedications(toggleOn) {
      if (toggleOn) {
        const updatedDosageForms = this.allMeds.map(med => {
          const existingDosageForm = (this.dosageForms || []).find(df => df.medicationId === med.id);
          if (existingDosageForm) {
            return existingDosageForm;
          } else {
            return {
              id: chance.guid(),
              medicationId: med.id,
              dosageFormType: med.dosageForm,
              concentration: med.defaultConcentration,
              enabledRoutes: this.getDefaultRoutes(med),
              administeringUnits: [ this.getDefaultAdministeringUnits(med) ],
            };
          }
        });
        this.$emit('updateCase', { dosageForms: updatedDosageForms });
      } else {
        this.$emit('updateCase', {dosageForms: []});
      }
    },
    selectItem(item, params) {
      if (params.isChild) {
        this.selectMedication(item);
      } else {
        this.selectGroup(item);
      }
    },
    performAction(action) {
      const { groupKey, medicationId } = action;
      if (!groupKey || !medicationId) {
        return;
      }
      this.state.newDosageGroup = this.findGroup(groupKey);
      this.showNewDosageDialog = true;
    },
    selectMedication(medItem) {
      const med = this.findMedication(medItem.key);
      this.$emit('select', med);
    },
    selectGroup(groupItem) {
      // The group expanded state is stored separately from the
      // medication items so that it persists when the medication
      // items are recomputed.
      const id = groupItem.key;
      if (!this.state.expandedMap[id]) {
        this.state.expandedMap[id] = true;
      } else {
        delete this.state.expandedMap[id];
      }
    },
    getDefaultRoutes(med) {
      return med.routes
        .map(route => {
          const medRoute = med.routes.find(medRoute => medRoute.key === route.key);
          const options = {};
          if (medRoute) {
            if (medRoute.key === 'IV_IO_INFUSION') {
              options.infusionDosingTypes = cloneDeep(medRoute.defaultDosingTypes);
              options.infusionBagVolumes = cloneDeep(medRoute.defaultBagVolumes);
              options.formularyEnabled = !!(medRoute.defaultFormularies && medRoute.defaultFormularies.length);
              options.formularies = cloneDeep(medRoute.defaultFormularies);
            } else if (medRoute.key === 'IV_IO_PUSH') {
              options.pushTimeframes = cloneDeep(medRoute.defaultPushTimeframes);
            }
          }
          return {
            key: route.key,
            options
          };
        });
    },
    getDefaultAdministeringUnits(med) {
      if (med.defaultAdministeringUnit) {
        return med.defaultAdministeringUnit;
      }
      const dosageForm = dosageForms.find(df => df.key == med.dosageForm);
      return dosageForm.unitType2 ? dosageForm.unitType2 : med.defaultConcentration.numeratorUnits;
    },
    cancelNewMedication() {
      this.showNewMedDialog = false;
    },
    submitNewMedication(newMedData) {
      this.showNewMedDialog = false;
      const groupKey = chance.guid();
      const newMedGroup = {
        key: groupKey,
        label: {
          en: newMedData.name
        }
      };
      const updateMedGroups = cloneDeep(this.customGroups) || [];
      updateMedGroups.push(newMedGroup);

      const newMed = this.createCustomMed(newMedGroup, newMedData);
      const updatedCustomMeds = cloneDeep(this.customMedications) || [];
      updatedCustomMeds.push(newMed);

      const updatedDosageForms = cloneDeep(this.dosageForms) || [];
      const newDosageForm = {
        id: chance.guid(),
        medicationId: newMed.key,
        dosageFormType: newMedData.dosageFormType,
        concentration: newMedData.concentration,
        enabledRoutes: [],
        administeringUnits: [ newMedData.concentration.numeratorUnits ],
      };
      updatedDosageForms.push(newDosageForm);
      this.$emit('updateCase', {
        dosageForms: updatedDosageForms,
        customMedGroups: updateMedGroups,
        customMedications: updatedCustomMeds
      });
      this.$emit('select', newMed);
    },
    cancelNewDosage() {
      this.showNewDosageDialog = false;
      this.state.newDosageGroup = null;
    },
    submitNewDosage(newDosageData) {
      const newMed = this.createCustomMed(this.state.newDosageGroup, newDosageData);
      const updatedCustomMeds = cloneDeep(this.customMedications) || [];
      updatedCustomMeds.push(newMed);
      const newDosageForm = {
        id: chance.guid(),
        medicationId: newMed.key,
        dosageFormType: newDosageData.dosageFormType,
        concentration: newDosageData.concentration,
        enabledRoutes: [],
      };
      const updatedDosageForms = cloneDeep(this.dosageForms) || [];
      updatedDosageForms.push(newDosageForm);

      this.$emit('updateCase', {
        dosageForms: updatedDosageForms,
        customMedications: updatedCustomMeds
      });
      this.$emit('select', newMed);

      this.showNewDosageDialog = false;
      this.state.newDosageGroup = null;
    },
    createCustomMed(group, dosageData) {
      const newMedKey = chance.guid();
      const df = dosageForms.find(df => df.key === dosageData.dosageFormType);
      const newMedName = `${group.label.en} - ${df.label.en}`;
      const newCustomMed = {
        id: newMedKey,
        key: newMedKey,
        name: {
          en: newMedName
        },
        routes: [],
        group: group.key,
        dosageForm: dosageData.dosageFormType,
        defaultConcentration: dosageData.concentration
      };
      return newCustomMed;
    },
    sortMedications() {
      const isEnabled = (m) => !!(this.dosageForms || []).find(df => df.medicationId === m.id || df.medicationId === m.key);
      this.state.sortedMeds = sortBy(this.allMeds, [med => isEnabled(med) ? 0 : 1], 'name.en');
    },
    findMedication(medicationKey) {
      let med = this.medications.find(m => m.id === medicationKey);
      if (!med) {
        med = this.customMedications.find(cm => cm.key === medicationKey || cm.id === medicationKey);
      }
      return med;
    },
    findGroup(groupKey) {
      let group = medicationGroups.find(g => g.key === groupKey);
      if (!group) {
        group = (this.customGroups || []).find(g => g.key === groupKey);
      }
      return group;
    },
  },
};
</script>

<style scoped>
.case-medication-list {
  min-width: 200px;
  max-width: 350px;
}

.switch-tree {
  height: 100%;
  min-width: 300px;
}

.medication-subpanel-header {
  border-bottom: 1px solid darkgray;
  padding-bottom: 10px;
}

.scroll-content {
  overflow-y: auto;
  overflow-x: hidden;
  height: calc(100% - 62px);
}

.name-header {
  padding-left: 10px;
  border-left: 1px solid darkgray;
}

.new-med-row {
  display: flex;
  align-items: center;
}

.add-button {
  margin-top: 0px;
  margin-bottom: 0px;
}
</style>
